// src/store/apis/banners.api.ts
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

// Small helper if you need conditional credentials
const withAuthHeader = (headers) => {
  const token =
    localStorage.getItem("_at")
  if (token) headers.set("Authorization", `Bearer ${token}`)
  return headers                       // IMPORTANT: return headers
}

export const BannersApi = createApi({
  reducerPath: "BannersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL,
    prepareHeaders: (headers) => withAuthHeader(headers),
  }),
  tagTypes: ["Banners"],
  keepUnusedDataFor: 600,          // Increased to 10 minutes for better caching
  refetchOnFocus: false,           // avoid surprise refetches while typing
  refetchOnReconnect: true,        // handy if the user was offline
  endpoints: (builder) => ({
    listAll: builder.query({
      // stable serialization avoids unnecessary re-renders on same params
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page = 1, limit = 10, search = "" } = queryArgs ?? {}
        return `${endpointName}-${page}-${limit}-${search}`
      },
      query: ({ page = 1, limit = 10, search = "" } = {}) =>
        `/banner?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`,
      transformResponse: (resp) => resp, // passthrough (but place to map/normalize)
      // granular tags -> only refetch the list OR an updated item
      providesTags: (result) =>
        result?.result
          ? [
              ...result.result.map((b) => ({ type: "Banners" , id: b._id })),
              { type: "Banners" , id: "LIST" },
            ]
          : [{ type: "Banners" , id: "LIST" }],
    }),

    listForHome: builder.query({
      query: () => "/banner/list",      
      keepUnusedDataFor: 300, // home banners change rarely — cache longer (5 minutes)
      // Provide tags for better cache management
      providesTags: [{ type: "Banners", id: "LIST" }],
      // Transform response to ensure consistent structure
      transformResponse: (response) => response,
    }),

    showById: builder.query({
      query: (id) => `/banner/${id}`,
      providesTags: (result, _err, id) => [{ type: "Banners", id }],
    }),

    create: builder.mutation({
      query: (formData) => ({
        url: "/banner",
        method: "POST",
        body: formData,
      }),
      // invalidate only the list (and possibly new id if response returns it)
      invalidatesTags: (_res) => [{ type: "Banners", id: "LIST" }],
    }),

    update: builder.mutation({
      query: ({ id, payload }) => ({ url: `/banner/${id}`, method: "PUT", body: payload }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Banners", id },
        { type: "Banners", id: "LIST" },
      ],
    }),

    delete: builder.mutation({
      query: (id) => ({
        url: `/banner/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Banners", id },
        { type: "Banners", id: "LIST" },
      ],
    }),
    reorder:builder.mutation({
      query:(orderedIds)=>({
        url:"/banner/reorder",
        method:"PUT",
        body:{orderedIds}
      })
    })
  }),
})

export const {
  useListAllQuery,
  useListForHomeQuery,
  useShowByIdQuery,
  useCreateMutation,
  useUpdateMutation,
  useDeleteMutation,
  useLazyListAllQuery,     // on-demand fetching
  useReorderMutation
} = BannersApi
